import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface PipelineFilters {
  consultantId?: string | null;
  sdrId?: string | null;
  source?: string | null;
  channel?: string | null;
  createdFrom?: Date | null;
  createdTo?: Date | null;
  businessUnit?: string | null;
}

export interface StageCount {
  stage: string;
  count: number;
  value: number;
  avg_days: number;
}

export interface ClosingVelocity {
  stage: 'WON' | 'LOST';
  avg_days: number;
  median_days: number;
  count: number;
}

export interface DailyDeal {
  date: string;
  count: number;
  value: number;
}

export interface PipelineTotals {
  active_count: number;
  active_value: number;
  won_count: number;
  won_value: number;
  lost_count: number;
  lost_value: number;
  total_count: number;
  win_rate: number;
  loss_rate: number;
}

export interface PipelineDashboardMetrics {
  stage_counts: StageCount[];
  totals: PipelineTotals;
  closing_velocity: ClosingVelocity[];
  daily_deals: DailyDeal[];
}

export function usePipelineMetrics(filters: PipelineFilters = {}) {
  return useQuery({
    queryKey: [
      'pipeline-metrics',
      filters.consultantId,
      filters.sdrId,
      filters.source,
      filters.channel,
      filters.createdFrom?.toISOString(),
      filters.createdTo?.toISOString(),
      filters.businessUnit,
    ],
    queryFn: async () => {
      console.log('📊 [PipelineMetrics RPC] Fetching with filters:', filters);
      
      const { data, error } = await supabase.rpc('get_pipeline_dashboard_metrics', {
        p_consultant_id: filters.consultantId || null,
        p_sdr_id: filters.sdrId || null,
        p_source: filters.source || null,
        p_channel: filters.channel || null,
        p_created_from: filters.createdFrom?.toISOString() || null,
        p_created_to: filters.createdTo?.toISOString() || null,
        p_business_unit: filters.businessUnit || null,
      });

      if (error) {
        console.error('❌ [PipelineMetrics RPC] Error:', error);
        throw error;
      }

      const metrics = data as unknown as PipelineDashboardMetrics;

      console.log('✅ [PipelineMetrics RPC] Success:', {
        stages: metrics?.stage_counts?.length,
        totalDeals: metrics?.totals?.total_count,
      });

      return metrics;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    refetchOnWindowFocus: false,
  });
}
